"use client";

import Image from "next/image";
import { ArrowUpRight } from "@phosphor-icons/react";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

interface ServiceCard {
	keyPrefix: string;
	image: string;
	href: string;
	imageAspectRatio?: string;
}

interface IndustryServicesProps {
	namespace: string;
	services: readonly ServiceCard[];
	brandColor?: string;
	titleClassName?: string;
}

export default function IndustryServices({
	namespace,
	services,
	brandColor = "#64a525",
	titleClassName = "",
}: IndustryServicesProps) {
	const t = useTranslations(namespace);
	const defaultImageAspectRatio = "250/250";

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2
						className={`mx-auto mb-3 max-w-[850px] text-2xl font-bold leading-[1.3] text-black sm:mb-4 sm:text-[30px] sm:leading-[1.28] md:text-4xl md:leading-[1.25] ${titleClassName}`}
					>
						{t("servicesTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("servicesDesc")}</p>
				</header>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4 lg:gap-6">
					{services.map((service) => (
						<div
							key={service.keyPrefix}
							className="flex flex-col overflow-hidden bg-[#fcfff4] shadow-card transition-shadow duration-300 hover:shadow-md"
						>
							<div
								className="relative overflow-hidden bg-[#f5f5f5]"
								style={{ aspectRatio: service.imageAspectRatio ?? defaultImageAspectRatio }}
							>
								<Image
									src={service.image}
									alt={t(`${service.keyPrefix}Alt`)}
									fill
									className="object-contain"
									sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
								/>
							</div>
							<div className="flex flex-1 flex-col p-5 sm:p-6">
								<h3 className="mb-2 text-lg font-bold text-black sm:mb-3 sm:text-xl">
									{t(`${service.keyPrefix}Title`)}
								</h3>
								<p className="mb-4 flex-1 text-sm leading-relaxed text-black">
									{t(`${service.keyPrefix}Desc`)}
								</p>
								<Link
									href={service.href}
									className="inline-flex items-center gap-1 text-sm font-semibold transition-colors duration-200 hover:opacity-75"
									style={{ color: brandColor }}
								>
									{t("learnMore")}
									<ArrowUpRight size={16} weight="bold" />
								</Link>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
